/**
 * InstructorInsightsPage.jsx
 * 
 * Shareable Instructor Insights page with cover image background
 * (like dietary/accessibility preference card shareables).
 * 
 * Data shown is controlled by the HOST via instructor_insights_visibility toggles.
 * Nothing is hardcoded - all data flows from Admin template > Host event > this page.
 * 
 * Question bank: Host curates which questions instructor sees.
 * Host can also add their own custom questions for the instructor.
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Users,
  BarChart3,
  MessageSquare,
  ArrowLeft,
  GraduationCap,
  Briefcase,
  Target,
  Zap,
  Clock,
  Calendar,
  Eye,
  Lock,
  MapPin,
  Share,
  Copy,
  Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return url.startsWith('/') ? `${API}${url}` : url;
};

export default function InstructorInsightsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [insights, setInsights] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [hostQuestions, setHostQuestions] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [visibility, setVisibility] = useState({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const eventRes = await fetch(`${API}/events/${eventId}`, { headers });
        if (!eventRes.ok) {
          toast.error("Event not found");
          navigate('/');
          return;
        }
        const eventData = await eventRes.json();
        setEvent(eventData);
        
        // Get visibility settings from host
        const vis = eventData.instructor_insights_visibility || eventData.settings?.instructor_insights_visibility || {};
        setVisibility(vis);

        const instructorEmail = (eventData.instructor?.email || "").toLowerCase();
        const userEmail = (user?.email || "").toLowerCase();
        
        if ((instructorEmail && userEmail && instructorEmail === userEmail) ||
            eventData.host_user_id === user?.user_id ||
            user?.role === 'admin' || user?.role === 'super_admin') {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
          setLoading(false);
          return;
        }

        // Fetch audience insights (only what host has enabled)
        const insightsRes = await fetch(`${API}/events/${eventId}/audience-insights`, { headers });
        if (insightsRes.ok) {
          setInsights(await insightsRes.json());
        }

        // Fetch question bank (curated by host for instructor)
        const questionsRes = await fetch(`${API}/events/${eventId}/question-bank`, { headers });
        if (questionsRes.ok) {
          const qData = await questionsRes.json();
          // Only show questions host has shared with instructor
          setQuestions((qData.questions || []).filter(q => q.shared_with_instructor));
        }
        
        // Fetch host custom questions for instructor
        const hostQRes = await fetch(`${API}/events/${eventId}/host-questions-for-instructor`, { headers });
        if (hostQRes.ok) {
          const hqData = await hostQRes.json();
          setHostQuestions(hqData.questions || []);
        }

      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (eventId && isAuthenticated) {
      fetchData();
    } else if (!isAuthenticated) {
      setLoading(false);
    }
  }, [eventId, user, isAuthenticated, navigate]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-500">Loading insights...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2">Login Required</h2>
            <p className="text-gray-600 mb-4">Please log in to view session insights.</p>
            <Button onClick={() => navigate('/')}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">Only the session instructor can view these insights.</p>
            <Button variant="outline" onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalRSVPs = insights?.total_rsvps || 0;
  const roleBreakdown = insights?.role_breakdown || {};
  const stageBreakdown = insights?.stage_breakdown || {};
  const interestBreakdown = insights?.interest_breakdown || {};
  const accessibilityBreakdown = insights?.accessibility_breakdown || {};
  const coverImage = getImageUrl(event?.cover_image || event?.card_background);
  const logoImage = getImageUrl(event?.logo_image);
  
  // Category-based background colors for fallback
  const categoryGradients = {
    instructor: 'from-indigo-900 via-purple-900 to-slate-900',
    makeup: 'from-pink-900 via-rose-900 to-slate-900',
    hair: 'from-amber-900 via-orange-900 to-slate-900',
    nails: 'from-fuchsia-900 via-pink-900 to-slate-900',
    style: 'from-violet-900 via-purple-900 to-slate-900',
    dj: 'from-cyan-900 via-blue-900 to-slate-900',
    culinary: 'from-orange-900 via-red-900 to-slate-900',
    events: 'from-emerald-900 via-teal-900 to-slate-900',
    venue: 'from-stone-900 via-gray-900 to-slate-900',
    travel: 'from-sky-900 via-blue-900 to-slate-900',
  };
  const fallbackGradient = categoryGradients[event?.category || event?.pilot_category] || 'from-gray-900 via-slate-800 to-black';

  // Filter sections based on host visibility settings
  const isVisible = (key) => visibility[key] !== false;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="instructor-insights-page">
      {/* Hero with Cover Image Background (like dietary/accessibility shareables) */}
      <div className="relative" data-testid="insights-hero">
        {coverImage ? (
          <>
            <div className="absolute inset-0">
              <img 
                src={coverImage} 
                alt={event?.name || 'Event'} 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
            </div>
          </>
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-b ${fallbackGradient}`} />
        )}
        
        <div className="relative px-4 py-8 sm:px-6 sm:py-12 max-w-3xl mx-auto text-white">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-8">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-2">
              <Badge className="bg-white/20 text-white border-white/30">
                <Eye className="w-3 h-3 mr-1" /> Read Only
              </Badge>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleCopyLink}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          {/* Event Details (mirrors welcome page) */}
          <div className="flex items-start gap-4 mb-6">
            {logoImage && (
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-white/20 border-2 border-white/30 flex-shrink-0">
                <img src={logoImage} alt={event?.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium tracking-wider text-white/60 uppercase mb-1">
                {event?.org_name || event?.host_organization || 'Session'}  - Insights
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2">{event?.name || event?.title || "Session"}</h1>
              {event?.description && (
                <p className="text-sm text-white/80 line-clamp-2 mb-3">{event.description}</p>
              )}
            </div>
          </div>
          
          {/* Event Meta (mirrors welcome page details) */}
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/80">
            {event?.date && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            {(event?.start_time || event?.time) && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {event.start_time || event.time}{event?.end_time ? ` - ${event.end_time}` : ''}
              </span>
            )}
            {(event?.location || event?.venue) && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                {event.location || event.venue}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              {totalRSVPs} registered
            </span>
          </div>
          
          {/* Hosted By */}
          {(event?.host_name || event?.org_name) && (
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-xs text-white/50 mb-1">Hosted by</p>
              <p className="text-sm font-medium">{event.org_name || event.host_name}</p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-3">
          <Card data-testid="stat-registered">
            <CardContent className="pt-5 pb-4 text-center">
              <p className="text-2xl font-bold">{totalRSVPs}</p>
              <p className="text-xs text-gray-500 mt-1">Registered</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-questions">
            <CardContent className="pt-5 pb-4 text-center">
              <p className="text-2xl font-bold">{questions.length + hostQuestions.length}</p>
              <p className="text-xs text-gray-500 mt-1">Questions</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-interests">
            <CardContent className="pt-5 pb-4 text-center">
              <p className="text-2xl font-bold">{Object.keys(interestBreakdown).length}</p>
              <p className="text-xs text-gray-500 mt-1">Interest Areas</p>
            </CardContent>
          </Card>
        </div>

        {/* Audience Roles - only if host enabled */}
        {isVisible("audience_roles") && Object.keys(roleBreakdown).length > 0 && (
          <Card data-testid="section-roles">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Briefcase className="w-4 h-4 text-indigo-500" />
                Audience Roles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(roleBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{role}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(count / totalRSVPs) * 100}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-8">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Experience Levels - only if host enabled */}
        {isVisible("experience_levels") && Object.keys(stageBreakdown).length > 0 && (
          <Card data-testid="section-stages">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap className="w-4 h-4 text-amber-500" />
                Experience Levels
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stageBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([stage, count]) => (
                  <div key={stage} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{stage}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(count / totalRSVPs) * 100}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-8">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Topics/Interests - only if host enabled */}
        {isVisible("topics_demand") && Object.keys(interestBreakdown).length > 0 && (
          <Card data-testid="section-interests">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Target className="w-4 h-4 text-green-500" />
                Topic Interest & Demand
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(interestBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([interest, count]) => (
                  <Badge key={interest} variant="secondary" className="py-1.5 px-3">
                    {interest}
                    <span className="ml-1.5 text-xs opacity-70">({count})</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Accessibility Summary - only if host enabled */}
        {isVisible("accessibility_summary") && Object.keys(accessibilityBreakdown).length > 0 && (
          <Card data-testid="section-accessibility">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="w-4 h-4 text-blue-500" />
                Accessibility Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(accessibilityBreakdown).sort((a, b) => b[1] - a[1]).map(([need, count]) => (
                  <div key={need} className="flex items-center justify-between py-1">
                    <span className="text-sm text-gray-700">{need}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Curated Questions from Attendees - only if host enabled */}
        {isVisible("questions_curated") && (
          <Card data-testid="section-questions">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="w-4 h-4 text-purple-500" />
                Questions from Attendees
              </CardTitle>
              <CardDescription>Curated by your host</CardDescription>
            </CardHeader>
            <CardContent>
              {questions.length > 0 ? (
                <div className="space-y-3">
                  {questions.map((q, idx) => (
                    <div key={q.id || idx} className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-gray-900 font-medium mb-2">"{q.question || q.text}"</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{q.attendee_name || 'Anonymous'}</span>
                        <span>{q.submitted_at ? new Date(q.submitted_at).toLocaleDateString() : ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No curated questions yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Host Questions for Instructor */}
        {hostQuestions.length > 0 && (
          <Card data-testid="section-host-questions">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <GraduationCap className="w-4 h-4 text-orange-500" />
                Questions from Host
              </CardTitle>
              <CardDescription>Your host would like to know</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {hostQuestions.map((q, idx) => (
                  <div key={q.id || idx} className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                    <p className="text-gray-900 font-medium">{q.question || q.text}</p>
                    {q.context && (
                      <p className="text-xs text-gray-500 mt-1">{q.context}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No data state */}
        {!insights && questions.length === 0 && hostQuestions.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="font-semibold text-lg mb-2">Insights Coming Soon</h3>
              <p className="text-sm text-gray-500">Data will appear as attendees RSVP to this event.</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
